import React, { FC, Fragment, useState, useEffect } from 'react';
import cn from 'classnames';
import { css } from 'styled-components';

import { notEmpty } from '@crehana/utils';

import { useIsScrolled } from '@crehana/react-hooks';

import { Row, Col } from '@crehana/ui';

import type { CountryPrefix } from '@crehana/ts-types';
import { UserDegreeSegmentationEnum } from '@crehana/web/dist/__generated__/graphql-schema';
import {
  SEARCH_DEFAULT_COURSE_SECTION_COUNT,
  SEARCH_CATEGORIES_RECOMMENDED_COUNT,
} from '@crehana/web/dist/MainMenu/constants';
import { useMainMenuContext } from '@crehana/web/dist/MainMenu/hooks';
import useMainMenuSearchTranslations from '@crehana/web/dist/MainMenu/components/Search/components/SearchContent/hooks/useMainMenuSearchTranslations';

import NavbarContainer from '@crehana/web/dist/MainMenu/ui/NavbarContainer';
import SearchLoader from '@crehana/web/dist/MainMenu/components/Search/components/SearchContent/ui/SearchLoader';
import SearchResultsTitle from '@crehana/web/dist/MainMenu/components/Search/components/SearchContent/sections/SearchResultsTitle';
import BestSellerCourses from '@crehana/web/dist/MainMenu/components/Search/components/SearchContent/sections/BestSellerCourses';
import NewCourses from '@crehana/web/dist/MainMenu/components/Search/components/SearchContent/sections/NewCourses';
import RecommendationsByCategory from '@crehana/web/dist/MainMenu/components/Search/components/SearchContent/sections/RecommendationsByCategory';

import { useUITheme } from 'Jsx/ContextApis/UITheme';
import { useMe } from 'Jsx/ContextApis/AuthContext';
import ScrollTopFAB from 'Jsx/Shared/ScrollTopFAB';

import { useSearchCoursesQuery } from '../hooks';
import removeMainMenuSourceDetail from '../utils/removeMainMenuSourceDetail';
import CourseListSkeleton from '../ui/CourseListSkeleton';
import SearchResults from '../sections/SearchResults';
import { SEARCH_LANDING_AMPLITUDE_PAGE_NAME } from '../constants';

type SearchLandingProps = {
  countryPrefix: CountryPrefix;
  searchTerm: string;
};

const SearchLanding: FC<SearchLandingProps> = ({
  countryPrefix,
  searchTerm,
}) => {
  const { isScrolled } = useIsScrolled();

  const { isDark } = useUITheme();
  const { me, loading: loadingMe } = useMe();

  const { t } = useMainMenuSearchTranslations();
  const { urls, currentBreakpointKey } = useMainMenuContext();

  const { loading, totalResults, results, hasMore, onFetchMore } =
    useSearchCoursesQuery({
      searchTerm,
    });

  const [shouldRenderPhoneLeadCard, setShouldRenderPhoneLeadCard] =
    useState(false);

  const hidePhoneLeadCard = () => {
    setShouldRenderPhoneLeadCard(false);
  };

  useEffect(() => {
    const isAnonymous = !me;
    const isPAYG =
      me &&
      !me.isMentor &&
      !me.isProfessor &&
      !me.isPremiumMember &&
      me.userDegreeSegmentation !==
        UserDegreeSegmentationEnum.ACTIVATED_DEGREE_SEGMENTATION &&
      !me.hasOrganization;

    if (!loadingMe && (isAnonymous || isPAYG) && countryPrefix !== 'br') {
      setShouldRenderPhoneLeadCard(true);
    }
  }, [loadingMe, me, countryPrefix]);

  const renderTitle = () => {
    if (!searchTerm) {
      return (
        <NavbarContainer>
          <Row>
            <Col>
              <h5
                className={cn(
                  'font-h4-reg mb-16 md:mb-32',
                  isDark ? 'text-gray-main' : 'text-gray-dark',
                )}
              >
                {t('SEARCH_RESULTS_EMPTY_RECOMMENDATION_TITLE')}
              </h5>
            </Col>
          </Row>
        </NavbarContainer>
      );
    }
    if (loading && !results.length) {
      return <SearchLoader className="mb-16" isDark={isDark} />;
    }
    return (
      <SearchResultsTitle
        className="mb-16 md:mb-32"
        isDark={isDark}
        totalResults={totalResults}
      />
    );
  };

  const renderCourses = () => {
    const textColorClassName = isDark ? 'text-white' : 'text-base-main';
    const courseCount =
      SEARCH_DEFAULT_COURSE_SECTION_COUNT[currentBreakpointKey];
    const catalogUrl = removeMainMenuSourceDetail({
      url: urls.catalog,
    });
    const newCoursesCatalogUrl = removeMainMenuSourceDetail({
      url: urls.newCoursesCatalog,
    });
    const userCategoryTastes = (me?.catalogCategoryTastes || [])
      .filter(notEmpty)
      .slice(0, SEARCH_CATEGORIES_RECOMMENDED_COUNT);

    if (loading && !results.length) {
      return (
        <NavbarContainer>
          <CourseListSkeleton isDark={isDark} />
        </NavbarContainer>
      );
    }
    if (results.length) {
      return (
        <SearchResults
          textColorClassName={textColorClassName}
          isDark={isDark}
          shouldRenderPhoneLeadCard={shouldRenderPhoneLeadCard}
          hidePhoneLeadCard={hidePhoneLeadCard}
          results={results}
          hasMore={hasMore}
          onFetchMore={onFetchMore}
          source={SEARCH_LANDING_AMPLITUDE_PAGE_NAME}
        />
      );
    }

    if (userCategoryTastes.length) {
      return (
        <Fragment>
          {userCategoryTastes.map(category => (
            <RecommendationsByCategory
              key={`recommendation-by-category-${category.originalId}`}
              className="mb-40"
              isDark={isDark}
              textColorClassName={textColorClassName}
              courseCount={courseCount}
              categoryId={category.originalId || -1}
              categoryName={category.name}
              catalogUrl={catalogUrl}
            />
          ))}
        </Fragment>
      );
    }
    return (
      <Fragment>
        <BestSellerCourses
          className="mb-40 md:mb-24"
          textColorClassName={textColorClassName}
          isDark={isDark}
          shouldRenderPhoneLeadCard={shouldRenderPhoneLeadCard}
          hidePhoneLeadCard={hidePhoneLeadCard}
          courseCount={courseCount}
          catalogUrl={catalogUrl}
        />
        <NewCourses
          textColorClassName={textColorClassName}
          isDark={isDark}
          courseCount={courseCount}
          catalogUrl={newCoursesCatalogUrl}
        />
      </Fragment>
    );
  };

  return (
    <div className="flex flex-col py-16 sm:py-32">
      {renderTitle()}
      {renderCourses()}
      <NavbarContainer
        className={`sticky bottom-0 left-0 right-0 w-full flex justify-end h-0 overflow-visible z-7 ${
          isScrolled ? 'cui-is-visible' : 'cui-is-hidden'
        }`}
        css={css`
          margin-top: 152px;
          ${({ theme }) => theme.breakpoints.up('sm')} {
            margin-top: 112px;
          }
        `}
      >
        <div className="relative w-full h-full overflow-visible">
          <ScrollTopFAB
            className="absolute right-0"
            css={css`
              bottom: 80px;
              ${({ theme }) => theme.breakpoints.up('sm')} {
                bottom: 40px;
              }
            `}
          />
        </div>
      </NavbarContainer>
    </div>
  );
};

export default SearchLanding;
